import React, { useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog, ResponsiveRows, SectionCard } from '../ui';
import { useToast } from '../../contexts/ToastContext';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { apiErrorMessage } from './barcodeOverrideApi';

type Correction = {
  id: string;
  integrationName: string | null;
  platform: string;
  productName: string;
  productSku: string | null;
  oldBarcodes: string[];
  newBarcode: string;
  listingState: 'carries_old' | 'unconfirmed';
  canFix: boolean;
  fixMessage: string;
  lastError: string | null;
};

async function call(path: string, method = 'GET') {
  const headers = await getAuthHeaders();
  const res = await makeApiCall(path, { method, headers });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(apiErrorMessage(json, res.status));
  return json;
}

const QUERY_KEY = ['store-barcode-corrections'];

const CorrectionList: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => call('/store/barcode-corrections') as Promise<{ data: Correction[]; canDecide: boolean }>,
  });
  const [confirm, setConfirm] = useState<{ row: Correction; action: 'fix' | 'dismiss' } | null>(null);
  const act = useMutation({
    mutationFn: async () => call(`/store/barcode-corrections/${confirm!.row.id}/${confirm!.action}`, 'POST'),
    onSuccess: (json) => {
      showToast(json?.data?.message || t('store.barcodeCorrections.dismissed'), 'success');
      setConfirm(null);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error: Error) => {
      showToast(error.message, 'error');
      setConfirm(null);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const rows = data?.data || [];
  if (rows.length === 0) return null;
  const canDecide = data?.canDecide === true;
  return (
    <SectionCard dense title={t('store.barcodeCorrections.title')} subtitle={t('store.barcodeCorrections.intro')}>
      <ResponsiveRows
        rows={rows}
        getRowKey={(row) => row.id}
        columns={[
          { key: 'product', header: t('store.barcodeCorrections.columns.product'), width: 'minmax(0,1.4fr)' },
          { key: 'connection', header: t('store.barcodeCorrections.columns.connection'), width: 'minmax(0,1fr)', mobileLabel: true },
          { key: 'barcode', header: t('store.barcodeCorrections.columns.barcode'), width: 'minmax(0,1.4fr)', mobileLabel: true },
          { key: 'action', header: t('store.barcodeCorrections.columns.action'), width: 'minmax(0,1.6fr)', mobileLabel: true },
        ]}
        renderCell={(row, column) => {
          if (column === 'product') {
            return (
              <>
                <Typography variant="body2">{row.productName}</Typography>
                {row.productSku ? <Typography variant="caption" color="text.secondary">{row.productSku}</Typography> : null}
              </>
            );
          }
          if (column === 'connection') return row.integrationName || row.platform;
          if (column === 'barcode') {
            return (
              <>
                <Typography variant="body2">
                  <code>{row.oldBarcodes.join(' / ')}</code> {t('store.barcodeCorrections.to')} <code>{row.newBarcode}</code>
                </Typography>
                {row.listingState === 'unconfirmed' ? (
                  <Typography variant="caption" color="text.secondary">{t('store.barcodeCorrections.unconfirmed')}</Typography>
                ) : null}
              </>
            );
          }
          return (
            <Stack spacing={0.5} alignItems="flex-start">
              {!row.canFix ? <Typography variant="caption">{row.fixMessage}</Typography> : null}
              {row.lastError ? <Alert severity="warning" sx={{ py: 0 }}>{row.lastError}</Alert> : null}
              {canDecide ? (
                <Stack direction="row" spacing={1}>
                  {row.canFix ? (
                    <Button size="small" variant="contained" onClick={() => setConfirm({ row, action: 'fix' })}>
                      {t('store.barcodeCorrections.fix')}
                    </Button>
                  ) : null}
                  <Button size="small" onClick={() => setConfirm({ row, action: 'dismiss' })}>
                    {t('store.barcodeCorrections.keep')}
                  </Button>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">{t('store.barcodeCorrections.askOwner')}</Typography>
              )}
            </Stack>
          );
        }}
      />
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.action === 'fix' ? t('store.barcodeCorrections.fixTitle') : t('store.barcodeCorrections.keepTitle')}
        message={confirm?.action === 'fix'
          ? t('store.barcodeCorrections.fixBody', {
            product: confirm.row.productName,
            connection: confirm.row.integrationName || confirm.row.platform,
            barcode: confirm.row.newBarcode,
          })
          : t('store.barcodeCorrections.keepBody')}
        confirmLabel={confirm?.action === 'fix' ? t('store.barcodeCorrections.fix') : t('store.barcodeCorrections.keep')}
        loading={act.isPending}
        onConfirm={() => act.mutate()}
        onClose={() => setConfirm(null)}
      />
    </SectionCard>
  );
};

/** Flag `barcode_override` (MOT-838): barcodes Motzu corrected that this store's listings may still carry. */
export const BarcodeCorrectionsCard: React.FC = () => {
  const { enabled } = useOrgFeatureFlag('barcode_override');
  return enabled ? <CorrectionList /> : null;
};
